/**
 * Polyline Utilities
 *
 * Utilities for encoding and decoding Google's polyline format.
 * Based on Google's polyline encoding algorithm.
 */

/**
 * Decodes Google's encoded polyline format to lat/lng coordinates
 * Returns plain objects (not google.maps.LatLng) for calculation purposes
 *
 * Google Polyline Encoding Algorithm:
 * ===================================
 *
 * The algorithm uses three key techniques for compression:
 *
 * 1. DELTA ENCODING
 *    Instead of storing absolute coordinates, stores differences between consecutive points.
 *    This results in smaller numbers which compress better.
 *    Example: [38.5, -120.2, 38.51, -120.21] → [38.5, -120.2, 0.01, -0.01]
 *
 * 2. PRECISION SCALING
 *    Multiplies coordinates by 1e5 to convert to integers (preserving 5 decimal places).
 *    Example: 38.50000 → 3850000
 *
 * 3. VARIABLE-LENGTH INTEGER ENCODING
 *    Each coordinate is encoded as a series of bytes where:
 *    - Each byte stores 5 bits of data (0x1f mask = 0b11111)
 *    - Bit 6 (0x20 = 0b100000) indicates if more bytes follow (continuation bit)
 *    - Values are offset by 63 to avoid ASCII control characters
 *
 * 4. ZIG-ZAG ENCODING (for signed integers)
 *    Converts signed integers to unsigned by interleaving positive/negative values:
 *    0 → 0, -1 → 1, 1 → 2, -2 → 3, 2 → 4, ...
 *    Formula: (n << 1) XOR (n >> 31)
 *    Decode:  (n & 1) ? ~(n >> 1) : (n >> 1)
 *
 * Example Encoding Flow:
 * ----------------------
 * Coordinate: 38.5
 * 1. Scale: 38.5 * 100000 = 3850000
 * 2. Zig-zag: 3850000 → 7700000 (since positive, multiply by 2)
 * 3. Variable-length encode:
 *    7700000 in binary needs multiple 5-bit chunks
 *    Each chunk encoded as ASCII char (offset +63)
 *    Continuation bit set on all but last byte
 *
 * This implementation reverses the process to extract lat/lng pairs.
 */
export function decodePolylineToCoords(encoded: string): Array<{ lat: number; lng: number }> {
  const points: Array<{ lat: number; lng: number }> = [];
  let index = 0;
  let lat = 0; // Accumulated latitude (delta-encoded)
  let lng = 0; // Accumulated longitude (delta-encoded)

  while (index < encoded.length) {
    // ═══════════════════════════════════════════════════════════
    // DECODE LATITUDE
    // ═══════════════════════════════════════════════════════════

    let b; // Current byte value
    let shift = 0; // Bit position for assembling the value
    let result = 0; // Accumulated result

    // Variable-length integer decoding:
    // Read bytes until we find one without the continuation bit (b < 0x20)
    do {
      b = encoded.charCodeAt(index++) - 63; // Convert ASCII to value (offset by 63)
      result |= (b & 0x1f) << shift; // Extract 5 data bits and shift into position
      shift += 5; // Move to next 5-bit position
    } while (b >= 0x20); // 0x20 (bit 6) is the continuation flag

    // Zig-zag decode: Convert unsigned back to signed
    // If LSB is 1: negative number, invert bits and shift right
    // If LSB is 0: positive number, just shift right
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat; // Add delta to accumulated latitude

    // ═══════════════════════════════════════════════════════════
    // DECODE LONGITUDE
    // ═══════════════════════════════════════════════════════════

    shift = 0;
    result = 0;

    // Same variable-length decoding process for longitude
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    // Zig-zag decode for longitude
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng; // Add delta to accumulated longitude

    // ═══════════════════════════════════════════════════════════
    // CONVERT TO DECIMAL AND STORE
    // ═══════════════════════════════════════════════════════════

    // Scale back from integer (1e5 precision) to decimal degrees
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;
}

/**
 * Decodes polyline and converts to Google Maps LatLng objects
 * Only use this when Google Maps API is available
 */
export function decodePolyline(encoded: string): Array<google.maps.LatLng> {
  const coords = decodePolylineToCoords(encoded);
  return coords.map((coord) => new google.maps.LatLng(coord.lat, coord.lng));
}
